/**
 * DriverDeliveryHistory — Phase 43.11
 *
 * Storico consegne completate dal driver. Lista paginata + totali in alto.
 * Usa endpoint GET /api/driver/delivery-history.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Package, Route as RouteIcon, Coins, ChevronRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { formatCHF, formatKm, formatDateTime, DELIVERY_TYPE_LABEL, SIZE_LABEL } from "@/lib/format";
import PullToRefreshContainer from "@/components/PullToRefreshContainer";

export default function DriverDeliveryHistory() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data: d } = await api.get("/driver/delivery-history");
      setData(d);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <PullToRefreshContainer onRefresh={load}>
      <div className="space-y-5" data-testid="driver-history-page">
        <Link to="/driver/profile" className="inline-flex items-center gap-1.5 text-sm font-semibold text-dapnow-muted hover:text-dapnow-ink">
          <ArrowLeft className="h-4 w-4" /> Profilo
        </Link>

        <header>
          <p className="overline">Driver</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1">Storico consegne</h1>
          <p className="text-sm text-dapnow-muted mt-1">Tutte le consegne che hai completato con successo.</p>
        </header>

        {loading && !data && (
          <div className="card-soft p-8 text-center text-dapnow-muted text-sm flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
          </div>
        )}

        {data && (
          <>
            {/* Totali */}
            <div className="grid sm:grid-cols-3 gap-3">
              <StatCard testid="history-total-deliveries" icon={Package} label="Consegne" value={data.total_deliveries} />
              <StatCard testid="history-total-earnings" icon={Coins} label="Guadagno totale" value={formatCHF(data.total_earnings_chf)} />
              <StatCard testid="history-total-km" icon={RouteIcon} label="Km totali" value={formatKm(data.total_distance_km)} />
            </div>

            {/* Lista */}
            {data.items?.length === 0 ? (
              <div className="card-soft p-8 text-center" data-testid="history-empty">
                <p className="font-semibold text-dapnow-ink">Nessuna consegna completata.</p>
                <p className="text-sm text-dapnow-muted mt-1">Quando completerai la tua prima consegna apparirà qui.</p>
              </div>
            ) : (
              <ul className="space-y-2" data-testid="history-list">
                {data.items.map((o, i) => (
                  <li key={o.id || i}>
                    <Link
                      to={`/driver/orders/${o.id}`}
                      data-testid={`history-row-${o.id}`}
                      className="card-soft p-4 flex items-center gap-3 hover:border-dapnow-blue/40 transition"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-bold tracking-tight text-sm truncate">
                          #{(o.id || "").slice(0, 8).toUpperCase()}
                          <span className="text-dapnow-muted font-normal ml-2">
                            · {o.pickup_struct?.city || "—"} → {o.delivery_struct?.city || "—"}
                          </span>
                        </p>
                        <p className="text-xs text-dapnow-muted truncate">
                          {DELIVERY_TYPE_LABEL[o.delivery_type] || o.delivery_type} · {SIZE_LABEL[o.package_size] || o.package_size} · {formatKm(o.distance_km)}
                        </p>
                        <p className="text-[11px] text-dapnow-muted/80 mt-0.5">
                          {formatDateTime(o.delivered_at || o.created_at)}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-display font-bold tracking-tight text-sm">{formatCHF(o.driver_earning)}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-dapnow-muted flex-shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </PullToRefreshContainer>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="card-soft p-4" data-testid={testid}>
      <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-dapnow-blue-soft text-dapnow-blue">
        <Icon className="h-4 w-4" />
      </div>
      <p className="overline mt-3">{label}</p>
      <p className="font-display text-2xl font-extrabold tracking-tighter mt-0.5">{value}</p>
    </div>
  );
}
